<script setup>
import { ref, computed, reactive, onMounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useWidgets } from '@store/widgets';
import { Group, Tabs, InputLabel, InputGroup, IconUploader, Text, Button, Select, Toggle, NewBadge, Textarea, ColorPicker, Spin, MessagePreset, Tooltip } from '@components';
import API from '@util/API'
import { useTranslation } from '../../composables/useTranslation';

const { t } = useTranslation();

const state = reactive({})
const $widget = useWidgets();
const { fields: widgetFieldsRef } = storeToRefs($widget);

const formychat_admin_vars = window.formychat_admin_vars || {};

// reCAPTCHA config (only visible when FormyChat form is selected).
// Use getter to read current values - formychat_admin_vars is updated after save.
const getRc = () => (formychat_admin_vars.recaptcha || {})
const recaptcha = reactive({
    enabled: getRc().enabled || false,
    site_key: getRc().site_key || '',
    secret_key: '',
    version: getRc().version || 'v2'
});

const recaptchaVersionOptions = computed(() => [
    { value: 'v2', name: t('recaptcha_version_v2'), label: t('recaptcha_version_v2') },
    { value: 'v3', name: t('recaptcha_version_v3'), label: t('recaptcha_version_v3') }
]);

const SECRET_KEY_MASK = '••••••••••••••••';
const userClearedSecretKey = ref(false);
const secretKeyDisplay = computed({
    get() {
        if ($widget.recaptchaSecretKeyConfigured && !recaptcha.secret_key && !userClearedSecretKey.value) {
            return SECRET_KEY_MASK;
        }
        return recaptcha.secret_key;
    },
    set(val) {
        const isMaskOrEmpty = val === '' || val === SECRET_KEY_MASK || /^•+$/.test(val);
        recaptcha.secret_key = isMaskOrEmpty ? '' : val;
        userClearedSecretKey.value = isMaskOrEmpty;
    }
});

// Cloudflare Turnstile (same scope as reCAPTCHA; only one should be active).
const getTs = () => (formychat_admin_vars.turnstile || {})
const turnstile = reactive({
    enabled: getTs().enabled || false,
    site_key: getTs().site_key || '',
    secret_key: ''
})
const userClearedTurnstileSecret = ref(false)
const turnstileSecretDisplay = computed({
    get() {
        if ($widget.turnstileSecretKeyConfigured && !turnstile.secret_key && !userClearedTurnstileSecret.value) {
            return SECRET_KEY_MASK;
        }
        return turnstile.secret_key;
    },
    set(val) {
        const isMaskOrEmpty = val === '' || val === SECRET_KEY_MASK || /^•+$/.test(val);
        turnstile.secret_key = isMaskOrEmpty ? '' : val;
        userClearedTurnstileSecret.value = isMaskOrEmpty;
    }
})

const errors = computed(() => $widget.errors || {});

const whatsapp = ref($widget.currentWidget.config?.whatsapp || {})
const icon = ref($widget.currentWidget.config?.icon || {})
const form = ref($widget.currentWidget.config?.form || {})
const cta = ref($widget.currentWidget.config?.cta || {})
const email = ref($widget.currentWidget.config?.email || {})

// Forms.
const cf7 = ref($widget.currentWidget.config?.cf7 || {})

const sizes = computed(() => ({
    'small': t('small'),
    'medium': t('medium'),
    'large': t('large'),
    'custom': t('custom'),
}));

const positions = computed(() => ({
    'left': t('left'),
    'right': t('right'),
    'custom': t('custom'),
}));

const admin_email = formychat_admin_vars.site.admin_email || '';

const allForms = formychat_admin_vars.data.forms || {}; 

// Form Mode.
const setFormMode = mode => {

    const customForms = Object.keys(allForms).filter(mode => mode !== 'formychat');

    // Reset errors.
    customForms.forEach(mode => {
        $widget.setError(mode + 'NotActivated', false)
        $widget.setError(mode + 'NotInstalled', false)
    })

    if (mode === 'formychat') {
        form.value.mode = mode
        $widget.fetchFields()
        return;
    }

    // Check if the form is installed and activated.
    if (customForms.includes(mode)) {
        if (!formychat_admin_vars.additional[mode].is_installed) {
            return $widget.setError(mode + 'NotInstalled')
        }

        if (!formychat_admin_vars.additional[mode].is_active) {
            return $widget.setError(mode + 'NotActivated')
        }

        // Set form.mode_form_id if not set.
        if (!form.value[mode + '_id']) {
            form.value[mode + '_id'] = $widget.contents.forms[mode][0]?.value || ''
        }
    }

    // Load form fields.
    form.value.mode = mode
    $widget.fetchFields()
}

const isLocked = computed(() => !formychat_admin_vars.is_premium)

const toggleDeviceVisibility = (device) => {
    if (isLocked.value) return

    // Get current values (default to true if undefined)
    const currentDesktop = icon.value.show_on_desktop !== false
    const currentMobile = icon.value.show_on_mobile !== false

    if (device === 'desktop') {
        // Don't allow disabling if mobile is also disabled
        if (currentDesktop && !currentMobile) return
        icon.value.show_on_desktop = !currentDesktop
    } else {
        // Don't allow disabling if desktop is also disabled
        if (currentMobile && !currentDesktop) return
        icon.value.show_on_mobile = !currentMobile
    }
}


// Activate CF7
const activatePlugin = async (plugin = 'cf7', action = 'install') => {
    state.pluginActivating = action;

    await API.get('action?action=activate_plugin&plugin=' + plugin);

    formychat_admin_vars.additional[plugin].is_active = true
    formychat_admin_vars.additional[plugin].is_installed = true

    setFormMode(plugin)
    await $widget.fetchContents();
    form.value[plugin + '_form_id'] = $widget.contents.forms[plugin].length > 0 ? $widget.contents.forms[plugin][0]?.value : ''
    // Load CF7 form.
    state.pluginActivating = false;
}

const YouTubeId = ref('')

// Forms.
const forms = formychat_admin_vars.data.forms || {}

// Sync recaptcha to store when FormyChat form is selected (for save).
watch(() => form.value.mode, (mode) => {
    if (mode === 'formychat') {
        const rc = getRc()
        recaptcha.enabled = rc.enabled || false
        recaptcha.site_key = rc.site_key || ''
        recaptcha.version = rc.version || 'v2'
        recaptcha.secret_key = ''
        userClearedSecretKey.value = false
        $widget.setRecaptchaOptions(recaptcha)

        const ts = getTs()
        turnstile.enabled = ts.enabled || false
        turnstile.site_key = ts.site_key || ''
        turnstile.secret_key = ''
        userClearedTurnstileSecret.value = false
        $widget.setTurnstileOptions(turnstile)
    }
}, { immediate: true })

watch(recaptcha, (val) => {
    if (form.value.mode === 'formychat') {
        $widget.setRecaptchaOptions({ ...val })
    }
}, { deep: true })

watch(turnstile, (val) => {
    if (form.value.mode === 'formychat') {
        $widget.setTurnstileOptions({ ...val })
    }
}, { deep: true })

watch(() => recaptcha.enabled, (on) => {
    if (form.value.mode !== 'formychat') return
    if (on && turnstile.enabled) {
        turnstile.enabled = false
    }
})

watch(() => turnstile.enabled, (on) => {
    if (form.value.mode !== 'formychat') return
    if (on && recaptcha.enabled) {
        recaptcha.enabled = false
    }
})

const defaultCustomDropdown = () => ({
    enabled: true,
    label: 'Dropdown',
    name: 'dropdown',
    required: false,
    placeholder: '- Select -',
    position: 'before_message',
    default_value: '',
    options: [
        { value: 'option_1', label: 'Option 1' },
        { value: 'option_2', label: 'Option 2' },
    ],
})

const ensureCustomDropdown = () => {
    if (!form.value.custom_dropdown || typeof form.value.custom_dropdown !== 'object') {
        form.value.custom_dropdown = defaultCustomDropdown()
    }
    if (!Array.isArray(form.value.custom_dropdown.options)) {
        form.value.custom_dropdown.options = []
    }
    if (form.value.custom_dropdown.default_value === undefined) {
        form.value.custom_dropdown.default_value = ''
    }
}

const customDropdownPositionOptions = computed(() => [
    { value: 'after_name', name: t('custom_dropdown_position_after_name'), label: t('custom_dropdown_position_after_name') },
    { value: 'after_email', name: t('custom_dropdown_position_after_email'), label: t('custom_dropdown_position_after_email') },
    { value: 'after_phone', name: t('custom_dropdown_position_after_phone'), label: t('custom_dropdown_position_after_phone') },
    { value: 'before_message', name: t('custom_dropdown_position_before_message'), label: t('custom_dropdown_position_before_message') },
])

const formychatMessagePresetFields = computed(() => {
    ensureCustomDropdown()
    const raw = widgetFieldsRef.value
    const base = raw && typeof raw === 'object' ? { ...raw } : {}
    const fd = form.value.custom_dropdown
    if (form.value.mode === 'formychat' && fd?.enabled && fd.name) {
        const lbl = fd.label && String(fd.label).trim() ? fd.label : fd.name
        base[fd.name] = lbl
    }
    return base
})

const addCustomDropdownOption = () => {
    ensureCustomDropdown()
    const n = form.value.custom_dropdown.options.length + 1
    form.value.custom_dropdown.options.push({ label: `Option ${n}`, value: `option_${n}` })
}

const dropdownMinOptionToast = ref(false)
let dropdownToastTimer = null

// Controls visibility of the "value" column in the options list.
// When hidden, each option's value is kept in sync with its label.
const showValueFields = ref(false)

watch(showValueFields, (show) => {
    if (!show) {
        form.value.custom_dropdown?.options?.forEach((opt) => {
            opt.value = opt.label
        })
    }
})

watch(
    () => form.value.custom_dropdown?.options?.map((o) => o.label),
    (newLabels) => {
        if (!showValueFields.value && newLabels) {
            newLabels.forEach((label, idx) => {
                if (form.value.custom_dropdown?.options?.[idx]) {
                    form.value.custom_dropdown.options[idx].value = label
                }
            })
        }
    }
)

const removeCustomDropdownOption = (idx) => {
    ensureCustomDropdown()
    if (form.value.custom_dropdown.options.length <= 1) {
        dropdownMinOptionToast.value = true
        clearTimeout(dropdownToastTimer)
        dropdownToastTimer = setTimeout(() => { dropdownMinOptionToast.value = false }, 3000)
        return
    }
    form.value.custom_dropdown.options.splice(idx, 1)
}

const DROPDOWN_TEMPLATE_LINE_RE = /^\s*Dropdown:\s*\{[^}]+\}\s*$/

const stripDropdownTemplateLines = (text) => {
    if (!text || typeof text !== 'string') {
        return ''
    }
    const lines = text.split('\n')
    const filtered = lines.filter((line) => !DROPDOWN_TEMPLATE_LINE_RE.test(line))
    return filtered.join('\n').replace(/\n{3,}/g, '\n\n')
}

const appendDropdownTemplateLine = (text, fieldKey) => {
    const key = (fieldKey && String(fieldKey).trim()) || 'dropdown'
    const line = `Dropdown: {${key}}`
    const current = typeof text === 'string' ? text : ''
    const trimmedLines = current.split('\n').map((l) => l.trimEnd())
    if (trimmedLines.some((l) => l.trim() === line)) {
        return current
    }
    if (!current.length) {
        return `${line}\n`
    }
    const endsWithNl = current.endsWith('\n')
    return `${current}${endsWithNl ? '' : '\n'}${line}\n`
}

watch(
    () => !!form.value.custom_dropdown?.enabled,
    (enabled, wasEnabled) => {
        if (form.value.mode !== 'formychat') {
            return
        }
        if (wasEnabled === undefined) {
            return
        }
        ensureCustomDropdown()
        const tmpl = whatsapp.value.message_template
        if (enabled) {
            const key = form.value.custom_dropdown.name || 'dropdown'
            whatsapp.value.message_template = appendDropdownTemplateLine(tmpl, key)
        } else {
            whatsapp.value.message_template = stripDropdownTemplateLines(tmpl)
        }
    }
)

watch(() => form.value.mode, (mode) => {
    if (mode === 'formychat') {
        ensureCustomDropdown()
    }
}, { immediate: true })

watch(() => $widget.currentWidget?.id, () => {
    ensureCustomDropdown()
})

// Adjust cf7_id.
onMounted(() => {
    ensureCustomDropdown()
    if (cf7.value.form_id) {
        form.value.cf7_id = cf7.value.form_id

        // Reset cf7_id.
        cf7.value.form_id = ''
    }

    if (form.value.mode === 'formychat') {
        const rc = getRc()
        recaptcha.enabled = rc.enabled || false
        recaptcha.site_key = rc.site_key || ''
        recaptcha.version = rc.version || 'v2'
        userClearedSecretKey.value = false
        $widget.setRecaptchaOptions(recaptcha)

        const ts = getTs()
        turnstile.enabled = ts.enabled || false
        turnstile.site_key = ts.site_key || ''
        turnstile.secret_key = ''
        userClearedTurnstileSecret.value = false
        $widget.setTurnstileOptions(turnstile)
    }

    $widget.fetchFields()
})


</script>
<template>
    <div class="w-full h-full">
        <div class="flex flex-col gap-10 w-full">
            <!-- Customize Icon  -->
            <Group :title="t('customize_icon')" :gap="false">
                <InputGroup>
                    <InputLabel :hints="t('select_icon_size_hint')" locked> {{ t('custom_icon') }}  </InputLabel>
                    <IconUploader :class="{ 'formychat-locked child:pointer-events-none child:opacity-60': isLocked }"
                        :default="Image('whatsapp.svg')" v-model="icon.image_url"
                        v-model:custom="icon.image_url_custom" />
                </InputGroup>
                <InputGroup>
                    <InputLabel :hints="t('select_icon_size_hint')"> {{ t('icon_size') }} </InputLabel>
                    <Tabs :options="sizes" :locked="['custom']" width="83px" class="bg-gray-50" v-model="icon.size" />
                </InputGroup>
                <InputGroup :condition="icon.size == 'custom'">
                    <InputLabel :hints="t('select_icon_size_hint')"> {{ t('custom_icon_size') }} </InputLabel>
                    <div class="w-full flex items-center justify-between gap-2">
                        <input class="formychat-slider" type="range" v-model="icon.size_custom" min="20" max="300">
                        <span class="w-16">{{ icon.size_custom }}px</span>
                    </div>
                </InputGroup>
                <InputGroup>
                    <InputLabel :hints="t('icon_position_hint')"> {{ t('icon_position') }} </InputLabel>
                    <Tabs :options="positions" :locked="['custom']" width="83px" class="bg-gray-50"
                        v-model="icon.position" />
                </InputGroup>
                <InputGroup :condition="icon.position == 'custom'">
                    <InputLabel :hints="t('select_icon_position_hint')"> {{ t('custom_icon_position') }} </InputLabel>
                    <div class="flex items-center gap-6 flex-wrap">
                        <div v-for="direction in ['left', 'right', 'top', 'bottom']" :key="direction"
                            class="flex flex-col gap-2">
                            <InputLabel class="capitalize text-gray-500" :for="`icon_position_custom_${direction}`">{{
                                direction }}</InputLabel>
                            <div class="flex items-center gap-1 w-24"><Text :id="`icon_position_custom_${direction}`"
                                    type="number" class="text-sm w-20" placeholder="auto"
                                    v-model="icon.position_custom[direction]" /><span
                                    v-if="icon.position_custom[direction] !== ''" class="text-gray-400">px</span></div>
                        </div>
                    </div>
                </InputGroup>
                <InputGroup>
                    <InputLabel :hints="t('display_widget_hint')"> {{ t('display_floating_widget_on') }} </InputLabel>
                    <div class="flex items-center gap-3 text-slate-600 text-sm">
                        <div class="bg-white px-4 py-2 flex items-center gap-2 ring rounded cursor-pointer relative transition"
                            :class="{
                                'ring-indigo-600': icon.show_on_desktop !== false,
                                'ring-slate-100 hover:ring-slate-300': icon.show_on_desktop === false,
                                'formychat-locked': isLocked
                            }"
                            @click.prevent="toggleDeviceVisibility('desktop')">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25" />
                            </svg>
                            <span class="font-medium text-xs">{{ t('desktop') }}</span>
                            <transition name="bounce">
                                <svg v-if="icon.show_on_desktop !== false" xmlns="http://www.w3.org/2000/svg"
                                    class="w-4 fill-indigo-500" viewBox="0 0 16 16">
                                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                                </svg>
                            </transition>
                        </div>
                        <div class="bg-white px-4 py-2 flex items-center gap-2 ring rounded cursor-pointer relative transition"
                            :class="{
                                'ring-indigo-600': icon.show_on_mobile !== false,
                                'ring-slate-100 hover:ring-slate-300': icon.show_on_mobile === false,
                                'formychat-locked': isLocked
                            }"
                            @click.prevent="toggleDeviceVisibility('mobile')">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                            </svg>
                            <span class="font-medium text-xs">{{ t('mobile') }}</span>
                            <transition name="bounce">
                                <svg v-if="icon.show_on_mobile !== false" xmlns="http://www.w3.org/2000/svg"
                                    class="w-4 fill-indigo-500" viewBox="0 0 16 16">
                                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                                </svg>
                            </transition>
                        </div>
                    </div>
                </InputGroup>
            </Group>
            <!-- Divider  -->
            <div class="border-b-2 border-dashed border-gray-200 rounded"></div>
            <!-- Call to action  -->
            <Group :title="t('call_to_action')" :gap="false">
                <InputGroup>
                    <Toggle v-model="cta.enabled">{{ t('show_call_to_action') }}</Toggle>
                </InputGroup>
                <InputGroup :condition="cta.enabled">
                    <InputLabel> {{ t('call_to_action_text') }} </InputLabel>
                    <Text :placeholder="t('call_to_action_text')" v-model="cta.text" />
                </InputGroup>
                <InputGroup :condition="cta.enabled">
                    <InputLabel :hints="t('select_icon_size_hint')" locked>{{ t('cta_style') }}</InputLabel>
                    <div class="flex items-center gap-3"
                        :class="{ 'opacity-60 formychat-locked child:pointer-events-none': isLocked }">
                        <ColorPicker v-model="cta.color"> {{ t('text_color') }} </ColorPicker>
                        <ColorPicker v-model="cta.background_color"> {{ t('background_color') }} </ColorPicker>
                    </div>
                </InputGroup>
                <InputGroup :condition="cta.enabled">
                    <InputLabel :hints="t('select_icon_size_hint')"> {{ t('text_size') }}</InputLabel>
                    <Tabs class="bg-gray-50" :options="sizes" width="85px" v-model="cta.size" :locked="['custom']" />
                </InputGroup>
                <InputGroup :condition="cta.enabled && cta.size == 'custom'">
                    <InputLabel :hints="t('select_icon_size_hint')"> {{ t('custom_text_size') }} </InputLabel>
                    <div class="w-full flex items-center justify-between gap-2">
                        <input class="formychat-slider" type="range" min="8" max="72" v-model="cta.size_custom">
                        <span class="w-16">{{ cta.size_custom || 8 }}px</span>
                    </div>
                </InputGroup>
            </Group>
            <!-- Divider  -->
            <div class="border-b-2 border-dashed border-gray-200 rounded"></div>
            <!-- Customize Form  -->
            <Group :title="t('customize_form')" :gap="false" id="customize_form"
                v-if="!(whatsapp.agent_mode === 'agents' && whatsapp.click_agent_to_redirect)">
                <InputGroup>
                    <InputLabel> {{ t('choose_message_source') }} </InputLabel>
                    <div class="flex items-center flex-wrap gap-5  text-slate-600 text-medium text-sm">
                        <div class="bg-white w-28 h-24 flex flex-col items-center gap-1 justify-center ring rounded cursor-pointer relative transition"
                            v-for="(label, mode) in forms" @click.prevent="setFormMode(mode)"
                            :class="{ 'ring-indigo-600': form.mode == mode, 'ring-slate-100 hover:ring-slate-300': form.mode !== mode }">
                            <img :src="label.logo" class="w-10">
                            <span class="w-full text-center font-medium text-xs">{{ label.label }}</span>
                            <transition name="bounce">
                                <svg v-if="form.mode == mode" xmlns="http://www.w3.org/2000/svg"
                                    class="w-5 fill-indigo-500 text-white absolute right-2 top-2" viewBox="0 0 16 16">
                                    <path
                                        d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                                </svg>
                            </transition>
                        </div>
                    </div>
                </InputGroup>
                <!-- When CF7 is not installed or activated.  -->
                <transition name="no">
                    <div v-if="errors.cf7NotActivated || errors.cf7NotInstalled"
                        class="flex items-start gap-5 px-6 py-5 bg-gray-100 border border-gray-200 rounded text-gray-700 w-full break-words">
                        <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-10" viewBox="0 0 16 16">
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                            <path
                                d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
                        </svg>
                        <p class="font-medium text-[13px] tracking leading-relaxed" v-html="t('plugin_not_ready_error', {
                                plugin: 'Contact Form 7',
                                status: `<strong class='font-bold'>${errors.CF7NotInstalled ? t('installed_status') : t('activated_status')}</strong>`,
                                action: errors.CF7NotInstalled ? t('install_and_activate') : t('activate')
                            })"></p>
                        <div>
                            <Button :class="{ 'opacity-60 pointer-events-none': state.pluginActivating }"
                                @click.prevent="activatePlugin('cf7')">
                                <span class="flex items-center gap-2" v-if="state.pluginActivating">
                                    {{ errors.cf7NotInstalled ? t('installing') : t('activating') }}
                                    <Spin class="w-4 h-4" />
                                </span>
                                <span v-else>{{ errors.cf7NotInstalled ? t('install') : t('activate') }}</span>
                            </Button>
                        </div>
                    </div>
                </transition>
                <!-- When Gravity Form is not installed or activated -->
                <transition name="no">
                    <div v-if="errors.gravityNotActivated || errors.gravityNotInstalled"
                        class="flex items-start gap-5 px-6 py-5 bg-gray-100 border border-gray-200 rounded text-gray-700 w-full break-words">
                        <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-10" viewBox="0 0 16 16">
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                            <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
                        </svg>

                        <p class="font-medium text-[13px] tracking leading-relaxed" v-html="t('activate_plugin_forms', {
                                plugin: 'Gravity Forms', 
                                status: `<strong class='font-bold'>${t('activated_status')}</strong>` 
                            })"></p>
                        <div v-if="!errors.gravityNotInstalled && errors.gravityNotActivated">
                            <Button :class="{ 'opacity-60 pointer-events-none': state.pluginActivating }"
                                @click.prevent="activatePlugin('gravity')">
                                <span class="flex items-center gap-2" v-if="state.pluginActivating">
                                    {{ t('activating') }} <Spin class="w-4 h-4" />
                                </span>
                                <span v-else>{{ t('activate') }}</span>
                            </Button>
                        </div>
                    </div>
                </transition>
                <!-- When WPForms is not installed or activated.  -->
                <transition name="no">
                    <div v-if="errors.wpformsNotActivated || errors.wpformsNotInstalled"
                        class="flex items-start gap-5 px-6 py-5 bg-gray-100 border border-gray-200 rounded text-gray-700 w-full break-words">
                        <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-10" viewBox="0 0 16 16">
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                            <path
                                d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
                        </svg>

                        <p class="font-medium text-[13px] tracking leading-relaxed" v-html="t('plugin_not_ready_error', {
                                plugin: 'WPForms',
                                status: `<strong class='font-bold'>${errors.WPFormsNotInstalled ? t('installed_status') : t('activated_status')}</strong>`,
                                action: errors.WPFormsNotInstalled ? t('install_and_activate') : t('activate')
                            })"></p>
                        <div>
                            <Button :class="{ 'opacity-60 pointer-events-none': state.pluginActivating }"
                                @click.prevent="activatePlugin('wpforms')">
                                <span class="flex items-center gap-2" v-if="state.pluginActivating">
                                    {{ errors.wpformsNotInstalled ? t('installing') : t('activating') }}
                                    <Spin class="w-4 h-4" />
                                </span>
                                <span v-else>{{ errors.wpformsNotInstalled ? t('install') : t('activate') }}</span>
                            </Button>
                        </div>
                    </div>
                </transition>

                <!-- When forminator is not installed or activated.  -->
                <transition name="no">
                    <div v-if="errors.forminatorNotActivated || errors.forminatorNotInstalled"
                        class="flex items-start gap-5 px-6 py-5 bg-gray-100 border border-gray-200 rounded text-gray-700 w-full break-words">
                        <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-10" viewBox="0 0 16 16">
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                            <path
                                d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
                        </svg>

                        <p class="font-medium text-[13px] tracking leading-relaxed" v-html="t('plugin_not_ready_error', {
                                plugin: 'Forminator',
                                status: `<strong class='font-bold'>${errors.forminatorNotInstalled ? t('installed_status') : t('activated_status')}</strong>`,
                                action: errors.forminatorNotInstalled ? t('install_and_activate') : t('activate')
                            })"></p>
                        <div>
                            <Button :class="{ 'opacity-60 pointer-events-none': state.pluginActivating }"
                                @click.prevent="activatePlugin('forminator')">
                                <span class="flex items-center gap-2" v-if="state.pluginActivating">
                                    {{ errors.forminatorNotInstalled ? t('installing') : t('activating') }}
                                    <Spin class="w-4 h-4" />
                                </span>
                                <span v-else>{{ errors.forminatorNotInstalled ? t('install') : t('activate') }}</span>
                            </Button>
                        </div>
                    </div>
                </transition>

                <!-- When Fluent Forms is not installed or activated.  -->
                <transition name="no">
                    <div v-if="errors.fluentformNotActivated || errors.fluentformNotInstalled"
                        class="flex items-start gap-5 px-6 py-5 bg-gray-100 border border-gray-200 rounded text-gray-700 w-full break-words">
                        <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-10" viewBox="0 0 16 16">
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                            <path
                                d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
                        </svg>

                        <p class="font-medium text-[13px] tracking leading-relaxed" v-html="t('plugin_not_ready_error', {
                                plugin: 'Fluent Forms',
                                status: `<strong class='font-bold'>${errors.fluentformNotInstalled ? t('installed_status') : t('activated_status')}</strong>`,
                                action: errors.fluentformNotInstalled ? t('install_and_activate') : t('activate')
                            })"></p>
                        <div>
                            <Button :class="{ 'opacity-60 pointer-events-none': state.pluginActivating }"
                                @click.prevent="activatePlugin('fluentform')">
                                <span class="flex items-center gap-2" v-if="state.pluginActivating">
                                    {{ errors.fluentformNotInstalled ? t('installing') : t('activating') }}
                                    <Spin class="w-4 h-4" />
                                </span>
                                <span v-else>{{ errors.fluentformNotInstalled ? t('install') : t('activate') }}</span>
                            </Button>
                        </div>
                    </div>
                </transition>

                <!-- When Formidable is not installed or activated.  -->
                <transition name="no">
                    <div v-if="errors.formidableNotActivated || errors.formidableNotInstalled"
                        class="flex items-start gap-5 px-6 py-5 bg-gray-100 border border-gray-200 rounded text-gray-700 w-full break-words">
                        <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-10" viewBox="0 0 16 16">
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                            <path
                                d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
                        </svg>

                        <p class="font-medium text-[13px] tracking leading-relaxed" v-html="t('plugin_not_ready_error', {
                                plugin: 'Formidable',
                                status: `<strong class='font-bold'>${errors.formidableNotInstalled ? t('installed_status') : t('activated_status')}</strong>`,
                                action: errors.formidableNotInstalled ? t('install_and_activate') : t('activate')
                            })"></p>
                        <div>
                            <Button :class="{ 'opacity-60 pointer-events-none': state.pluginActivating }"
                                @click.prevent="activatePlugin('formidable')">
                                <span class="flex items-center gap-2" v-if="state.pluginActivating">
                                    {{ errors.formidableNotInstalled ? t('installing') : t('activating') }}
                                    <Spin class="w-4 h-4" />
                                </span>
                                <span v-else>{{ errors.formidableNotInstalled ? t('install') : t('activate') }}</span>
                            </Button>
                        </div>
                    </div>
                </transition>

                <!-- When Ninja Forms is not installed or activated.  -->
                <transition name="no">
                    <div v-if="errors.ninjaNotActivated || errors.ninjaNotInstalled"
                        class="flex items-start gap-5 px-6 py-5 bg-gray-100 border border-gray-200 rounded text-gray-700 w-full break-words">
                        <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-10" viewBox="0 0 16 16">
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                            <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
                        </svg>

                        <p class="font-medium text-[13px] tracking leading-relaxed" v-html="t('plugin_not_ready_error', {
                                plugin: 'Ninja Forms',
                                status: `<strong class='font-bold'>${errors.ninjaNotInstalled ? t('installed_status') : t('activated_status')}</strong>`,
                                action: errors.ninjaNotInstalled ? t('install_and_activate') : t('activate')
                            })"></p>
                        <div>
                            <Button :class="{ 'opacity-60 pointer-events-none': state.pluginActivating }"
                                @click.prevent="activatePlugin('ninja')">
                                <span class="flex items-center gap-2" v-if="state.pluginActivating">
                                    {{ errors.ninjaNotInstalled ? t('installing') : t('activating') }}
                                    <Spin class="w-4 h-4" />
                                </span>
                                <span v-else>{{ errors.ninjaNotInstalled ? t('install') : t('activate') }}</span>
                            </Button>
                        </div>
                    </div>
                </transition>


                <!-- CF7 Forms  -->

                <InputGroup :condition="form.mode == 'cf7'">
                    <InputLabel> <span name="cf7_id">{{ t('select_form') }}</span> </InputLabel>
                    <div class="flex items-center gap-4">
                        <Select @select="errors.cf7_id = false, $widget.fetchFields()" :placeholder="t('select_form')" name="cf7_id"
                            v-model="form.cf7_id" :class="{ 'ring-red-500': errors.cf7_id }" class="max-w-fit min-w-max"
                            :cursor="true" :options="$widget.contents.forms.cf7 || []" />
                    </div>
                    <div>
                        {{ t('you_can') }} <a class="text-indigo-500" :href="`${formychat_admin_vars.site.url }/wp-admin/admin.php?page=wpcf7&post=${form.cf7_id}&action=edit#formychat`">{{ t('disable_email_notifications') }}</a> {{ t('from_selected_cf7_form') }}
                    </div>
                </InputGroup>

                <!-- Gravity Forms  -->
                <InputGroup :condition="form.mode == 'gravity'">
                    <InputLabel> <span name="gravity_form_id">{{ t('select_form') }}</span> </InputLabel>
                    <div class="flex items center gap-4">
                        <Select @select="errors.gravity_id = false, $widget.fetchFields()" :placeholder="t('select_form')"
                            name="gravity_form_id" v-model="form.gravity_id" class="max-w-fit min-w-52"
                            :options="$widget.contents.forms.gravity || []" />
                    </div>
                </InputGroup>
                <!-- WPForms  -->
                <InputGroup :condition="form.mode == 'wpforms'">
                    <InputLabel> <span name="wpforms_id">{{ t('select_form') }}</span> </InputLabel>
                    <div class="flex items center gap-4">
                        <Select @select="errors.wpforms_id = false, $widget.fetchFields()" :placeholder="t('select_form')"
                            name="wp_form_id" v-model="form.wpforms_id" class="max-w-fit min-w-max"
                            :options="$widget.contents.forms.wpforms || []" />
                    </div>
                </InputGroup>
                <!-- FluentForms  -->
                <InputGroup :condition="form.mode == 'fluentform'">
                    <InputLabel> <span name="fluentform_id">{{ t('select_form') }}</span> </InputLabel>
                    <div class="flex items center gap-4">
                        <Select @select="errors.fluentform_id = false, $widget.fetchFields()" :placeholder="t('select_form')"
                            name="fluentform_id" v-model="form.fluentform_id" class="max-w-fit min-w-max"
                            :options="$widget.contents.forms.fluentform || []" />
                    </div>
                </InputGroup>

                <!-- Forminator  -->
                <InputGroup :condition="form.mode == 'forminator'">
                    <InputLabel> <span name="forminator_id">{{ t('select_form') }}</span> </InputLabel>
                    <div class="flex items center gap-4">
                        <Select @select="errors.forminator_id = false, $widget.fetchFields()" :placeholder="t('select_form')"
                            name="forminator_id" v-model="form.forminator_id" class="max-w-fit min-w-max"
                            :options="$widget.contents.forms.forminator || []" />
                    </div>
                </InputGroup>

                <!-- Formidable  -->
                <InputGroup :condition="form.mode == 'formidable'">
                    <InputLabel> <span name="formidable_id">{{ t('select_form') }}</span> </InputLabel>
                    <div class="flex items center gap-4">
                        <Select @select="errors.formidable_id = false, $widget.fetchFields()" :placeholder="t('select_form')"
                            name="formidable_id" v-model="form.formidable_id" class="max-w-fit min-w-max"
                            :options="$widget.contents.forms.formidable || []" />
                    </div>
                </InputGroup>

                <!-- Ninja Forms  -->
                <InputGroup :condition="form.mode == 'ninja'">
                    <InputLabel> <span name="ninja_id">{{ t('select_form') }}</span> </InputLabel>
                    <div class="flex items center gap-4">
                        <Select @select="errors.ninja_id = false, $widget.fetchFields()" :placeholder="t('select_form')"
                            name="ninja_id" v-model="form.ninja_id" class="max-w-fit min-w-max"
                            :options="$widget.contents.forms.ninja || []" />
                    </div>
                </InputGroup>
                
                <!-- Message template  -->
                <InputGroup  :class="{
                    'opacity-60 pointer-events-none': state.loadingFields
                }">
                    <InputLabel :hints="t('message_template_hint')"> {{ t('message_template') }} </InputLabel>
                    <!-- FormyChat  -->

                    <!-- Message Preset  -->
                    <MessagePreset :form="form.mode" v-if="form.mode === 'formychat'" v-model="whatsapp.message_template"
                        :fields="formychatMessagePresetFields" />
                    <MessagePreset :form="form.mode" v-else v-model="whatsapp[ form.mode +'_message_template']"
                        :fields="$widget.fields" />
                </InputGroup>
                <!-- Receive leads via email  -->
                <InputGroup v-if="form.mode == 'formychat'">
                    <Toggle locked v-model="email.enabled"
                        :hints="t('receive_leads_email_hint')">
                        {{ t('receive_leads_via_email') }}</Toggle>
                </InputGroup>
                <InputGroup v-if="form.mode == 'formychat'" class="space-y-2" :condition="email.enabled || false">
                    <label><input type="checkbox" class="bg-transparent" v-model="email.admin_email"
                            :value="admin_email">{{ t('use_admin_email_receive') }}</label>
                    <Text v-if="email.admin_email" type="email" :placeholder="admin_email"
                        class="pointer-events-none" />
                    <Text name="email_address" v-model="email.address" :error="errors.email_address" v-else type="email"
                        :placeholder="t('enter_email_address')" />
                    <p class="text-sm text-sate-500 py-4 px-6 bg-gray-100 border border-gray-200 rounded"> <strong
                            class="text-semibold">{{ t('note') }}</strong> {{ t('please_configure_smtp') }} <a class="underline"
                            :href="`${formychat_admin_vars.site.url}/wp-admin/plugin-install.php?s=SMTP&tab=search&type=term`"
                            target="_blank">WP Mail SMTP</a> {{ t('or_text') }} <a class="underline"
                            :href="`${formychat_admin_vars.site.url}/wp-admin/plugin-install.php?s=SMTP&tab=search&type=term`"
                            target="_blank">FluentSMTP</a> {{ t('plugin_to_receive_mail') }} </p>
                </InputGroup>
                <!-- Custom dropdown (FormyChat form only) -->
                <InputGroup v-if="form.mode == 'formychat'">
                    <div class="flex items-center gap-3">
                        <Toggle v-model="form.custom_dropdown.enabled" :hints="t('custom_dropdown_hint')">
                            {{ t('custom_dropdown_field') }}</Toggle>
                        <NewBadge />
                    </div>
                    <div v-if="form.custom_dropdown.enabled" class="mt-3 rounded-lg bg-gray-100 p-4 space-y-4" style="border: 1.5px solid #6366f1;">
                        <!-- Header -->
                        <div class="flex items-center gap-1.5 mb-1" style="padding-bottom: 5px;">
                            <span class="text-sm font-semibold text-slate-700">{{ t('custom_dropdown_section_title') }}</span>
                            <Tooltip>{{ t('custom_dropdown_section_tooltip') }}</Tooltip>
                        </div>

                        <!-- Field label (full width) -->
                        <div>
                            <div class="flex items-center gap-1 mb-2">
                                <span class="text-xs font-medium text-slate-600">{{ t('custom_dropdown_field_label') }}</span>
                                <Tooltip>{{ t('custom_dropdown_field_label_tooltip') }}</Tooltip>
                            </div>
                            <Text :white="true" v-model="form.custom_dropdown.label" placeholder="Enter your Field label" />
                        </div>

                        <!-- Placeholder + Field position (2-col) -->
                        <div class="grid gap-3" style="grid-template-columns: 1fr 1fr">
                            <div>
                                <div class="flex items-center gap-1 mb-2">
                                    <span class="text-xs font-medium text-slate-600">Placeholder</span>
                                    <Tooltip>{{ t('custom_dropdown_placeholder_tooltip') }}</Tooltip>
                                </div>
                                <Text :white="true" v-model="form.custom_dropdown.placeholder" placeholder="- Select -" />
                            </div>
                            <div class="min-w-0">
                                <div class="flex items-center gap-1 mb-2">
                                    <span class="text-xs font-medium text-slate-600">{{ t('custom_dropdown_position') }}</span>
                                    <Tooltip>{{ t('custom_dropdown_position_tooltip') }}</Tooltip>
                                </div>
                                <Select size="w-full" :search="false" :white="true" :options="customDropdownPositionOptions" v-model="form.custom_dropdown.position" />
                            </div>
                        </div>

                        <!-- Required toggle -->
                        <div class="flex items-center justify-between gap-4 rounded-lg bg-white px-4 py-3" style="border: 1px solid rgb(209,211,213);">
                            <div>
                                <div class="flex items-center gap-1.5">
                                    <span class="text-sm font-semibold text-slate-700">{{ t('custom_dropdown_required') }}</span>
                                </div>
                                <p class="text-xs text-slate-400 mt-0.5">User must select an option to submit the form.</p>
                            </div>
                            <Toggle v-model="form.custom_dropdown.required" class="shrink-0" />
                        </div>

                        <!-- Options -->
                        <div>
                            <!-- OPTIONS title -->
                            <div class="flex items-center justify-between pt-3">
                                <div class="flex items-center gap-1.5">
                                    <span class="text-sm font-semibold text-slate-700">{{ t('custom_dropdown_options') }}</span>
                                    <Tooltip>{{ t('custom_dropdown_options_tooltip') }}</Tooltip>
                                </div>
                                <!-- Show Values checkbox -->
                                <label class="flex items-center gap-1.5 cursor-pointer select-none">
                                    <input type="checkbox" v-model="showValueFields"
                                        class="h-4 w-4 rounded border-slate-300 cursor-pointer accent-indigo-600"
                                        style="background-color: #fff;" />
                                    <span class="text-xs font-medium text-slate-500">Show Values</span>
                                    <Tooltip>When unchecked, each option's stored value is automatically kept in sync with its label. Check this to set a custom value independently from the label.</Tooltip>
                                </label>
                            </div>


                            <!-- Option rows -->
                            <div class="space-y-1.5 overflow-y-auto" style="max-height: 240px;">
                                <div v-for="(opt, idx) in form.custom_dropdown.options" :key="idx"
                                    class="flex items-center gap-2 rounded-md px-1.5 py-1 bg-transparent transition-colors">
                                    <!-- Default radio button -->
                                    <input type="radio"
                                        :value="opt.value"
                                        v-model="form.custom_dropdown.default_value"
                                        class="shrink-0 cursor-pointer accent-indigo-600"
                                        :style="form.custom_dropdown.default_value === opt.value
                                            ? 'background-color:#fff; outline: 1px solid #6366f1; outline-offset: 2px; border-radius: 50%; margin: 0 0.25rem 0 0 !important;'
                                            : 'background-color:#fff; margin: 0 0.25rem 0 0 !important;'"
                                        :title="'Set as default'" />
                                    <!-- Inputs -->
                                    <div class="grid flex-1 gap-2"
                                        :style="showValueFields ? 'grid-template-columns: 1fr 1fr' : 'grid-template-columns: 1fr'">
                                        <Text :white="true" v-model="opt.label" placeholder="label" />
                                        <Text v-if="showValueFields" :white="true" v-model="opt.value" placeholder="value" />
                                    </div>
                                    <!-- + / − action buttons -->
                                    <div class="flex items-center gap-1.5 shrink-0 mb-0.5">
                                        <button type="button" @click="addCustomDropdownOption"
                                            class="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-200 text-indigo-600 hover:bg-indigo-300 transition-colors font-bold text-base leading-none"
                                            :aria-label="t('custom_dropdown_add_option')">+</button>
                                        <button type="button" @click="removeCustomDropdownOption(idx)"
                                            class="flex h-7 w-7 items-center justify-center rounded-full transition-colors font-bold text-base leading-none"
                                            :class="form.custom_dropdown.options.length <= 1
                                                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                                : 'bg-red-200 text-red-600 hover:bg-red-300'"
                                            :aria-label="t('remove')">−</button>
                                    </div>
                                </div>
                            </div>

                            <!-- Clear default button -->
                            <div class="mt-2 flex items-center justify-end">
                                <button type="button"
                                    @click="form.custom_dropdown.default_value = ''"
                                    :disabled="!form.custom_dropdown.default_value"
                                    class="flex items-center gap-1.5 text-sm font-medium rounded-md px-3 py-1.5 transition-colors"
                                    :class="form.custom_dropdown.default_value
                                        ? 'bg-indigo-50 text-indigo-600 hover:bg-red-50 hover:text-red-500 cursor-pointer border border-indigo-100 hover:border-red-200'
                                        : 'bg-slate-100 text-slate-300 cursor-not-allowed border border-slate-100'">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                                    </svg>
                                    Clear selection
                                </button>
                            </div>

                            <!-- Min-option toast -->
                            <transition name="fade">
                                <div v-if="dropdownMinOptionToast"
                                    class="mt-2 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-1-5a1 1 0 0 0-1 1v2a1 1 0 1 0 2 0V9a1 1 0 0 0-1-1z" clip-rule="evenodd"/>
                                    </svg>
                                    At least one option must remain in the list.
                                </div>
                            </transition>
                        </div>
                    </div>
                </InputGroup>
                <!-- reCAPTCHA / Turnstile (only when FormyChat form is selected) -->
                <InputGroup v-if="form.mode == 'formychat'" class="space-y-4">
                    <div class="space-y-4">
                        <div class="space-y-2">
                            <div class="flex items-center gap-3">
                                <Toggle v-model="recaptcha.enabled" :hints="t('recaptcha_enable_hint')">
                                    {{ t('enable_recaptcha') }}
                                </Toggle>
                                <NewBadge />
                            </div>
                            <p
                                v-if="recaptcha.enabled"
                                class="text-xs text-slate-600 leading-relaxed pl-0.5 max-w-xl"
                            >
                                {{ t('recaptcha_setup_help') }}
                                <a
                                    href="https://docs.google.com/document/d/1Mpl0PDQyCmC34oS22r7nlDvfLkFoUjgaLzpGZDx2nGM/edit?usp=sharing"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="text-indigo-600 hover:text-indigo-800 font-medium underline decoration-indigo-300 underline-offset-2"
                                >{{ t('recaptcha_setup_documentation') }}</a>.
                            </p>
                        </div>
                        <div v-if="recaptcha.enabled" class="space-y-3 rounded-lg p-4" style="background: #f3f4f6; border: 1px solid #e5e7eb;">
                            <div>
                                <InputLabel class="mb-1 !items-start" semibold :hints="t('recaptcha_version_tooltip')">
                                    {{ t('recaptcha_version_label') }}
                                </InputLabel>
                                <Select v-model="recaptcha.version" :white="true" class="w-48" :options="recaptchaVersionOptions" :search="false" />
                                <p class="text-xs text-gray-500 mt-1">{{ t('recaptcha_version_hint') }}</p>
                            </div>
                            <div>
                                <InputLabel class="mb-1 !items-start" semibold :hints="t('recaptcha_site_key_tooltip')">
                                    {{ t('recaptcha_site_key_label') }}
                                </InputLabel>
                                <Text :white="true" v-model="recaptcha.site_key" :placeholder="t('recaptcha_site_key_placeholder')" type="text" />
                                <p class="text-xs text-gray-500 mt-1">{{ t('recaptcha_site_key_hint') }}</p>
                            </div>
                            <div>
                                <InputLabel class="mb-1 !items-start" semibold :hints="t('recaptcha_secret_key_tooltip')">
                                    {{ t('recaptcha_secret_key_label') }}
                                </InputLabel>
                                <Text :white="true" v-model="secretKeyDisplay"
                                    :placeholder="$widget.recaptchaSecretKeyConfigured ? '' : t('recaptcha_secret_key_placeholder')"
                                    type="password" />
                                <p class="text-xs text-gray-500 mt-1">{{ t('recaptcha_secret_key_hint') }}</p>
                            </div>
                        </div>
                        <div class="space-y-2">
                            <div class="flex items-center gap-3">
                                <Toggle v-model="turnstile.enabled" :hints="t('turnstile_enable_hint')">
                                    {{ t('enable_turnstile') }}
                                </Toggle>
                                <NewBadge />
                            </div>
                            <p
                                v-if="turnstile.enabled"
                                class="text-xs text-slate-600 leading-relaxed pl-0.5 max-w-xl"
                            >
                                {{ t('turnstile_setup_help') }}
                                <a
                                    href="https://docs.google.com/document/d/1tl2R3RXKo-pnauFKtJJSbU-5MedG7gI1FuYsrsTCaRY/edit?usp=sharing"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="text-indigo-600 hover:text-indigo-800 font-medium underline decoration-indigo-300 underline-offset-2"
                                >{{ t('turnstile_setup_documentation') }}</a>.
                            </p>
                        </div>
                        <div v-if="turnstile.enabled" class="space-y-3 rounded-lg p-4" style="background: #f3f4f6; border: 1px solid #e5e7eb;">
                            <div>
                                <InputLabel class="mb-1 !items-start" semibold :hints="t('turnstile_site_key_tooltip')">
                                    {{ t('turnstile_site_key_label') }}
                                </InputLabel>
                                <Text :white="true" v-model="turnstile.site_key" :placeholder="t('turnstile_site_key_placeholder')" type="text" />
                                <p class="text-xs text-gray-500 mt-1">{{ t('turnstile_site_key_hint') }}</p>
                            </div>
                            <div>
                                <InputLabel class="mb-1 !items-start" semibold :hints="t('turnstile_secret_key_tooltip')">
                                    {{ t('turnstile_secret_key_label') }}
                                </InputLabel>
                                <Text :white="true" v-model="turnstileSecretDisplay"
                                    :placeholder="$widget.turnstileSecretKeyConfigured ? '' : t('turnstile_secret_key_placeholder')"
                                    type="password" />
                                <p class="text-xs text-gray-500 mt-1">{{ t('turnstile_secret_key_hint') }}</p>
                            </div>
                        </div>
                    </div>
                </InputGroup>
                <!-- size  -->
                <InputGroup>
                    <InputLabel> {{ t('form_size') }} </InputLabel>
                    <Tabs :options="sizes" v-model="form.size" width="85px" class="bg-gray-50" />
                </InputGroup>
                <InputGroup :condition="form.size == 'custom'">
                    <InputLabel> {{ t('custom_form_size') }} </InputLabel>
                    <div class="w-full flex items-center justify-between gap-2">
                        <input class="formychat-slider" type="range" min="100" max="1000" v-model="form.size_custom">
                        <span class="w-16">{{ form.size_custom || 300 }}px</span>
                    </div>
                </InputGroup>
                <!-- Form  -->
                <InputGroup>
                    <InputLabel> {{ t('form_title_label') }} </InputLabel>
                    <Text :placeholder="t('form_title_placeholder')" v-model="form.title" name="form_title" />
                </InputGroup>
                <InputGroup>
                    <InputLabel :hints="t('leave_blank_disable')"> {{ t('header_text') }} </InputLabel>
                    <div class="w-full">
                        <Textarea :placeholder="t('header_text_placeholder')" class="resize-y"
                            name="form_subtitle" v-model="form.subtitle" />
                    </div>
                </InputGroup>
                <InputGroup>
                    <InputLabel :hints="t('leave_blank_disable')"> {{ t('footer_text') }} </InputLabel>
                    <div class="w-full">
                        <Textarea :placeholder="t('footer_text_placeholder')" class="resize-none"
                            name="form_footer" v-model="form.footer" />
                    </div>
                </InputGroup>
                <InputGroup :condition="form.mode == 'formychat'">
                    <InputLabel> {{ t('submit_button_text') }} </InputLabel>
                    <Text :placeholder="t('submit_button_placeholder')" name="form_submit" v-model="form.submit" />
                </InputGroup>
                <InputGroup class="space-y-3" :condition="form.mode == 'formychat'">
                    <div class="flex items-center gap-3">
                        <Toggle v-model="form.show_country_code_field"
                            :hints="t('country_code_hint')" locked> {{ t('allow_country_code_select') }} {{ t('country_code') }}</Toggle>
                    </div>
                    <transition name="slide">
                        <div class="flex items-center gap-2" v-if="form.show_country_code_field">
                            <InputLabel> {{ t('default') }} {{ t('country_code') }} </InputLabel>
                            <Select v-model="form.country_code" class="w-fit" :options="$widget.contents.countries"
                                :placeholder="t('select_country')" />
                        </div>
                    </transition>
                </InputGroup>
                <InputGroup>
                    <InputLabel locked> {{ t('widget_style') }} </InputLabel>
                    <div class="flex items-center gap-4"
                        :class="{ 'opacity-60 formychat-locked child:pointer-events-none': isLocked }">
                        <ColorPicker v-model="form.text_color">{{ t('text_color') }}</ColorPicker>
                        <ColorPicker v-model="form.background_color">{{ t('background_color') }}</ColorPicker>
                    </div>
                </InputGroup>
                <InputGroup class="w-full">
                    <InputLabel>{{ t('font_family') }}</InputLabel>
                    <Select :options="$widget.contents.fonts" v-model="form.font_family" :placeholder="t('select_font')"
                        class="w-56" />
                </InputGroup>
            </Group>
            <!-- Divider  -->
            <div class="border-b-2 border-dashed border-gray-200 rounded"></div>
            <!-- Form behavior  -->
            <Group :title="t('behavior')" :gap="false">
                <InputGroup class="space-y-3">
                    <Toggle v-model="form.open_by_default" :hints="t('form_open_default_hint')">
                        {{ t('open_by_default') }}</Toggle>
                    <Toggle v-model="form.close_on_submit" :hints="t('form_close_submit_hint')">{{ t('close_form_on_submit') }}
                        {{ t('on_submit') }}</Toggle>
                </InputGroup>
            </Group>
        </div>
        <!-- YouTube Video  -->
        <transition name="fade">
            <div v-if="YouTubeId" class="absolute left-0 top-0 w-full flex h-full z-20 items-center justify-center">
                <div class="absolute top-0 left-0 w-full h-full bg-black opacity-50" @click="YouTubeId = ''"></div>
                <div class="fixed top-1/2 -translate-y-1/2 w-full max-w-xl h-96 z-50">
                    <!-- close  -->
                    <div @click="YouTubeId = ''"
                        class="absolute -top-10 -right-10 cursor-pointer w-10 h-10 flex items-center justify-center text-white hover:brightness-110 hover:scale-110 transition">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                            class="h-5 w-5 fill-current">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                    </div>
                    <iframe class="w-full h-full"
                        :src="'https://www.youtube.com/embed/' + YouTubeId + '?autoplay=1'"></iframe>
                </div>
            </div>
        </transition>
    </div>
</template>
